/**
 * PM Tasks Overview Component
 * Displays PM Calendar tasks and activities in card format
 */

import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle, Clock, MapPin, Users, Repeat, Activity, Wrench } from 'lucide-react';
import { PMTask } from '../hooks/usePMTasks';

interface PMTasksOverviewProps {
  tasks: PMTask[];
  loading: boolean;
  error: string | null;
}

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
  low: { label: 'Low', color: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]', dot: 'bg-[#0084CF]' }
};

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', icon: Clock, color: 'text-[#0066A3]' },
  'in-progress': { label: 'In Progress', icon: Activity, color: 'text-amber-600' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-green-600' },
  overdue: { label: 'Overdue', icon: AlertCircle, color: 'text-red-600' },
  cancelled: { label: 'Cancelled', icon: AlertCircle, color: 'text-gray-500' }
};

export const PMTasksOverview: React.FC<PMTasksOverviewProps> = ({ tasks, loading, error }) => {
  const taskStats = useMemo(() => {
    return {
      total: tasks.length,
      scheduled: tasks.filter(t => t.status === 'scheduled').length,
      inProgress: tasks.filter(t => t.status === 'in-progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(t => t.status === 'overdue').length,
      recurring: tasks.filter(t => t.isRecurring).length
    };
  }, [tasks]);

  const upcomingTasks = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return tasks
      .filter(t => t.date >= today && (t.status === 'scheduled' || t.status === 'in-progress'))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 5);
  }, [tasks]);

  if (loading) {
    return (
      <div className="pm-tasks-section loading">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-tasks-section error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-800">Error Loading Tasks</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="pm-tasks-section empty">
        <div className="bg-[#F2F8FC] border border-[#C2DFF1] rounded-lg p-6 text-center">
          <Activity size={32} className="text-[#0066A3] mx-auto mb-2" />
          <p className="text-[#1C4D88] font-semibold">No PM Tasks</p>
          <p className="text-[#1C4D88] text-sm">Create PM Calendar tasks to see them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-tasks-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 items-center">
          <Wrench size={16} className="text-[#0066A3]" />
          <h3 className="pm-tasks-title text-lg font-semibold">PM Calendar Activities</h3>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
          {taskStats.total} tasks
        </span>
      </div>

      {/* Stats Row */}
      <div className="stats-row mb-6">
        <div className="stat-card stat-total">
          <div className="stat-number">{taskStats.total}</div>
          <div className="stat-label">Total Tasks</div>
        </div>
        <div className="stat-card stat-scheduled">
          <div className="stat-number">{taskStats.scheduled}</div>
          <div className="stat-label">Scheduled</div>
        </div>
        <div className="stat-card stat-in-progress">
          <div className="stat-number">{taskStats.inProgress}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-number">{taskStats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card stat-overdue">
          <div className="stat-number">{taskStats.overdue}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card stat-recurring">
          <div className="stat-number">{taskStats.recurring}</div>
          <div className="stat-label">Recurring</div>
        </div>
      </div>

      {/* Upcoming Tasks */}
      {upcomingTasks.length > 0 && (
        <div className="upcoming-tasks-section">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Upcoming Tasks</h4>
          <div className="upcoming-tasks-list">
            {upcomingTasks.map(task => {
              const StatusIcon = STATUS_CONFIG[task.status as keyof typeof STATUS_CONFIG]?.icon || AlertCircle;
              const statusConfig = STATUS_CONFIG[task.status as keyof typeof STATUS_CONFIG];
              const priorityConfig = PRIORITY_CONFIG[task.priority];

              return (
                <div key={task.id} className="task-item">
                  <div className="task-header">
                    <div className="task-info">
                      <h5 className="task-title">{task.title}</h5>
                      <p className="task-description text-xs text-gray-600 mt-1">{task.description}</p>
                    </div>
                    <span className={`status-badge ${priorityConfig?.color || ''}`}>
                      {priorityConfig?.label || task.priority}
                    </span>
                  </div>
                  
                  <div className="task-meta">
                    <div className="meta-item">
                      <Clock size={14} className="text-gray-500" />
                      <span className="text-xs text-gray-700">{task.date}</span>
                    </div>
                    {task.location && (
                      <div className="meta-item">
                        <MapPin size={14} className="text-gray-500" />
                        <span className="text-xs text-gray-700">{task.location}</span>
                      </div>
                    )}
                    {task.assignedTo && (
                      <div className="meta-item">
                        <Users size={14} className="text-gray-500" />
                        <span className="text-xs text-gray-700">{task.assignedTo}</span>
                      </div>
                    )}
                    {task.isRecurring && (
                      <div className="meta-item recurring-indicator">
                        <Repeat size={14} className="text-[#0066A3]" />
                        <span className="text-xs text-[#1C4D88]">
                          {task.recurringPattern ? task.recurringPattern : 'Recurring'}
                        </span>
                      </div>
                    )}
                    <div className={`meta-item status-indicator ${statusConfig?.color || ''}`}>
                      <StatusIcon size={14} />
                      <span className="text-xs font-medium">{statusConfig?.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Tasks Summary */}
      {tasks.length > upcomingTasks.length && (
        <div className="all-tasks-summary mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600">
            Showing {upcomingTasks.length} upcoming tasks out of <strong>{tasks.length} total</strong>
          </p>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .pm-tasks-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .pm-tasks-title {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #1e293b;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
        }

        .stat-card {
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          text-align: center;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: #cbd5e1;
          background: #f1f5f9;
          transform: translateY(-2px);
        }

        .stat-card.stat-total {
          background: linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%);
          border-color: #C2DFF1;
        }

        .stat-card.stat-scheduled {
          background: linear-gradient(135deg, #E3F0F9 0%, #f8fafc 100%);
          border-color: #8AC4E6;
        }

        .stat-card.stat-in-progress {
          background: linear-gradient(135deg, #fef3c7 0%, #f8fafc 100%);
          border-color: #fde047;
        }

        .stat-card.stat-completed {
          background: linear-gradient(135deg, #dcfce7 0%, #f8fafc 100%);
          border-color: #bbf7d0;
        }

        .stat-card.stat-overdue {
          background: linear-gradient(135deg, #fee2e2 0%, #f8fafc 100%);
          border-color: #fca5a5;
        }

        .stat-card.stat-recurring {
          background: linear-gradient(135deg, #E3F0F9 0%, #f8fafc 100%);
          border-color: #8AC4E6;
        }

        .stat-number {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .stat-label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .upcoming-tasks-section {
          border-top: 1px solid #e2e8f0;
          padding-top: 16px;
        }

        .upcoming-tasks-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .task-item {
          padding: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
          transition: all 0.2s ease;
        }

        .task-item:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
        }

        .task-info {
          flex: 1;
          min-width: 0;
        }

        .task-title {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .task-description {
          color: #64748b;
          margin: 4px 0 0 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .task-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #64748b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          white-space: nowrap;
        }

        .recurring-indicator {
          background: #E3F0F9;
          color: #0066A3;
          border: 1px solid #8AC4E6;
        }

        .status-indicator {
          background: #f1f5f9;
          color: #475569;
        }

        .pm-tasks-section.loading,
        .pm-tasks-section.error {
          padding: 24px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
        }

        .pm-tasks-section.empty {
          padding: 24px;
        }

        .all-tasks-summary {
          font-family: 'IBM Plex Sans', sans-serif;
          color: #64748b;
        }

        .all-tasks-summary strong {
          color: #0f172a;
          font-weight: 600;
        }
      `}} />
    </div>
  );
};
